import { Button } from "@nimit9/signet-ui/components/button"
import { Card, CardContent, CardFooter, CardHeader } from "@nimit9/signet-ui/components/card"
import { Input } from "@nimit9/signet-ui/components/input"
import { useRef, useState } from "react"
import { PaisaMark } from "@/components/common/paisa-mark"
import { useAcceptInvite, useHousehold } from "@/hooks/use-household"
import { ApiError } from "@/lib/api-errors"
import { signOut } from "@/lib/sign-out"
import { ONBOARDING_STEPS } from "./onboarding-steps"

function OnboardingShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-1 items-center justify-center bg-bg-base">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-accent-green/[0.03] blur-[120px]" />
      </div>
      <div className="relative flex flex-col items-center gap-6">
        {/* Same bars mark as login and join (the old "p." wordmark is retired). */}
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-green/10">
          <PaisaMark size={30} decorative />
        </span>
        {children}
      </div>
    </div>
  )
}

function OnboardingWizard({ onEnterWizard }: { onEnterWizard: () => void }) {
  const [step, setStep] = useState(0)
  const [mode, setMode] = useState<"choose" | "create" | "join">("choose")
  const [inviteToken, setInviteToken] = useState("")
  const [error, setError] = useState<string | null>(null)
  const acceptInvite = useAcceptInvite()

  const totalSteps = ONBOARDING_STEPS.length

  function handleNext() {
    if (step < totalSteps - 1) {
      setStep(step + 1)
    } else {
      window.location.reload()
    }
  }

  function handleSkip() {
    handleNext()
  }

  function extractToken(raw: string): string {
    try {
      const url = new URL(raw)
      return url.searchParams.get("token") || raw
    } catch {
      return raw.trim()
    }
  }

  async function handleJoin() {
    setError(null)
    const token = extractToken(inviteToken)
    if (!token) {
      setError("Please enter an invite token or URL")
      return
    }
    try {
      // Mark the wizard as entered BEFORE the mutation, which invalidates
      // ["household"] on success — see the comment on wizardEnteredRef for
      // why this ordering matters (#PH3).
      onEnterWizard()
      await acceptInvite.mutateAsync(token)
      // Joining an existing household skips setup: the household already has its
      // owners, accounts and privacy mode. Hand off to the app the same way the
      // wizard's last step does (a reload), instead of walking a joiner through
      // "Who manages money?" for a household that's already set up.
      window.location.assign("/")
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to accept invite")
    }
  }

  function handleSignOut() {
    signOut()
  }

  if (mode === "choose") {
    return (
      <OnboardingShell>
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <h1 className="text-[22px] font-semibold text-text-primary tracking-[-0.3px]">
              Welcome to Paisa
            </h1>
            <p className="text-sm text-text-secondary mt-1">
              Set up your household to get started, or join an existing one.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              className="w-full"
              onClick={() => {
                onEnterWizard()
                setMode("create")
              }}
            >
              Create a household
            </Button>
            <Button className="w-full" variant="outline" onClick={() => setMode("join")}>
              Join with an invite
            </Button>
          </CardContent>
          <CardFooter className="justify-center">
            <Button variant="ghost" size="sm" className="text-text-hint" onClick={handleSignOut}>
              Sign out
            </Button>
          </CardFooter>
        </Card>
      </OnboardingShell>
    )
  }

  if (mode === "join") {
    return (
      <OnboardingShell>
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <h1 className="text-[22px] font-semibold text-text-primary tracking-[-0.3px]">
              Join a household
            </h1>
            <p className="text-sm text-text-secondary mt-1">
              Paste the invite link or token you received.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              placeholder="Paste invite link or token"
              value={inviteToken}
              onChange={(e) => setInviteToken(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleJoin()}
              autoFocus
            />
            {error && <p className="text-sm text-accent-red">{error}</p>}
            <div className="flex gap-2">
              <Button
                variant="ghost"
                className="flex-1"
                onClick={() => {
                  setMode("choose")
                  setError(null)
                }}
              >
                Back
              </Button>
              <Button
                className="flex-1"
                onClick={handleJoin}
                disabled={acceptInvite.isPending || !inviteToken.trim()}
              >
                {acceptInvite.isPending ? "Joining..." : "Join"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </OnboardingShell>
    )
  }

  // Create flow — step wizard
  const currentStep = ONBOARDING_STEPS[step]
  const StepComponent = currentStep.component

  return (
    <OnboardingShell>
      <Card className="w-full max-w-sm">
        <CardContent className="pt-6 space-y-6">
          {/* Progress */}
          <div>
            <div className="flex items-center gap-1">
              {ONBOARDING_STEPS.map((s, i) => (
                <div
                  key={s.label}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    i <= step ? "bg-accent-green" : "bg-bg-elevated"
                  }`}
                />
              ))}
            </div>
            <p className="mt-2 text-xs text-text-hint">
              Step {step + 1} of {totalSteps} — {currentStep.label}
            </p>
          </div>

          <StepComponent onNext={handleNext} onSkip={handleSkip} />
        </CardContent>
      </Card>
    </OnboardingShell>
  )
}

export function OnboardingGate({ children }: { children: React.ReactNode }) {
  const { data, isLoading, isError, error, refetch, isFetching } = useHousehold()

  // Once the wizard has actually been entered (household create/join
  // submitted), a household appearing mid-flow must NOT pull the wizard out
  // from under the person before Owners/Account/Privacy/Import ever render.
  //
  // useCreateHousehold and useAcceptInvite both invalidate ["household"] on
  // success — the same query this gate reads — so without this guard the
  // gate flips to {children} the instant that query refetches, which is
  // right after the FIRST wizard step. Every step after Household (Owners,
  // Account, Privacy, and this phase's new "bring your first month in"
  // step) was consequently unreachable by a real user; only the wizard's own
  // final step (a hard `window.location.reload()`) should ever hand off.
  const wizardEnteredRef = useRef(false)

  // Also skip the loading spinner once inside the wizard: invalidating
  // ["household"] on an errored query (there's no household yet, so the GET
  // 404s) briefly flips this same query back to isLoading while it refetches
  // — confirmed by instrumenting a mount log, which fired twice more right
  // after the Household step's submit. Swapping OnboardingWizard out for the
  // spinner div, even for one commit, unmounts it and resets its local
  // step/mode state back to square one.
  if (!wizardEnteredRef.current && (isLoading || (isError && error?.name === "AuthError"))) {
    return (
      <div className="flex flex-1 items-center justify-center bg-bg-base">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-border-default border-t-accent-green" />
      </div>
    )
  }

  if (!wizardEnteredRef.current && !isError && data?.household) {
    return <>{children}</>
  }

  // Only a 404 means "no household yet". Any other failure (rate limit, server
  // error, offline) must not send an existing member into the create-household
  // wizard (UAT J2-02): say what happened and offer a retry.
  if (!wizardEnteredRef.current && isError && !isNoHousehold(error)) {
    return (
      <OnboardingShell>
        <Card className="w-full max-w-sm">
          <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
            <p className="text-body font-medium text-text-primary">Couldn't load your household</p>
            <p className="text-caption text-text-muted">
              {error instanceof ApiError && error.status === 429
                ? "Too many requests right now. Wait a moment and try again."
                : "Check your connection and try again."}
            </p>
            <Button size="touch" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? "Retrying…" : "Try again"}
            </Button>
          </CardContent>
        </Card>
      </OnboardingShell>
    )
  }

  return <OnboardingWizard onEnterWizard={() => (wizardEnteredRef.current = true)} />
}

function isNoHousehold(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404
}
